// react
import { useEffect } from 'react';
import styled, { css } from 'styled-components';

// components
import { Button, Container, Icon, LottieAnimation } from '@/components/atoms';
import { Icons } from '@/components/atoms/Icon/icons';
import {
  GenericFlowProps,
  OnboardingActions,
  OnboardingActionType,
  toGenericOnboarding,
} from '@/components/pages/OnboardingFlow/OnboardingFlow';

// assets
// @transform-path ../../../../../../src/assets/animation/confetti.json
import confettiAnimation from '@/assets/animation/confetti.json';

type BgColors = 'blue' | 'red' | 'green' | 'yellow';

const animationDuration = 1000;

export const OnboardingLoadingDone = toGenericOnboarding(
  ({
    config,
    currentIndex = 0,
    handleClick,
  }: GenericFlowProps<OnboardingLoadingDoneConfigItem>) => {
    return (
      <StyledContainer size='sm' color={config[currentIndex]?.color}>
        <Wrapper>
          {config[currentIndex]?.confetti && (
            <AnimationContainer>
              <LottieAnimation
                src={confettiAnimation}
                loop={true}
                autoplay={true}
              />
            </AnimationContainer>
          )}
          <ContentWrapper $isVisible={true}>
            {config[currentIndex]?.icon && (
              <ImageWrapper $bgColor={config[currentIndex]?.color}>
                <Icon fontSize={40} icon={config[currentIndex]?.icon} />
              </ImageWrapper>
            )}
            <Text>{config[currentIndex]?.text}</Text>
            {config[currentIndex]?.subtext && (
              <SubText>{config[currentIndex]?.subtext}</SubText>
            )}
            <ButtonWrapper $isVisible={true}>
              <Button
                size='md'
                isFullWidth={true}
                ariaLabel={config[currentIndex]?.ctaButtonLabel}
                onPress={() => handleClick?.(OnboardingActionType.Next)}
              >
                {config[currentIndex]?.ctaButtonLabel}
              </Button>
            </ButtonWrapper>
          </ContentWrapper>
        </Wrapper>
      </StyledContainer>
    );
  },
);

const ContentWrapper = styled.div<{ $isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${(props) => props.theme.spacing[32]};
  padding: ${(props) => props.theme.spacing[32]} 0;
  ${(props) =>
    !props.$isVisible &&
    css`
      opacity: 0;
      transform: scale(0.8);
    `}
  transition-duration: ${animationDuration / 2}ms;
`;

const ButtonWrapper = styled.div<{ $isVisible: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: ${(props) => props.theme.spacing[32]};
  ${(props) =>
    !props.$isVisible &&
    css`
      opacity: 0;
      transform: scale(0.8);
    `}
  transition-duration: ${animationDuration / 2}ms;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  justify-content: center;
  padding: 0 ${(props) => props.theme.spacing[24]};
`;

const StyledContainer = styled(Container)<{ color: BgColors }>`
  background: ${(props) => props.theme.color.cards.yellow.secondary};
  transition-duration: ${animationDuration}ms;
  height: unset;
  flex: 1;
`;

const AnimationContainer = styled.div`
  transform: scale(2);
  position: fixed;
  pointer-events: none;
`;

const ImageWrapper = styled.div<{ $bgColor: BgColors }>`
  width: 72px;
  height: 72px;
  min-width: 72px;
  min-height: 72px;
  max-width: 72px;
  max-height: 72px;
  border-radius: 999999px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${(props) => props.theme.color.cards.yellow.secondary};
  background: ${(props) => props.theme.color.cards[props.$bgColor]?.tertiary};
  padding: ${(props) => props.theme.spacing[12]};
`;

const Text = styled.div`
  font: ${(props) => props.theme.typography.body.lg.semibold};
  color: ${(props) => props.theme.color.neutral[100]};
  text-align: center;
`;

const SubText = styled(Text)`
  font: ${(props) => props.theme.typography.body.sm.regular};
  color: ${(props) => props.theme.color.neutral[100]};
  text-align: center;
`;

export interface OnboardingLoadingDoneConfigItem extends OnboardingActions {
  id: string;
  icon: Icons;
  text: string;
  subtext?: string;
  color: BgColors;
  confetti?: boolean;
  ctaButtonLabel: string;
  ctaButtonPath?: string;
}

OnboardingLoadingDone.displayName = 'OnboardingLoadingDone';
export default toGenericOnboarding(OnboardingLoadingDone);
